<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';

import Logo from 'next/icon/Logo.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SidebarAccountSwitcher from 'dashboard/components-next/sidebar/SidebarAccountSwitcher.vue';
import SidebarNotificationBell from 'dashboard/components-next/sidebar/SidebarNotificationBell.vue';
import SidebarProfileMenu from 'dashboard/components-next/sidebar/SidebarProfileMenu.vue';

const emit = defineEmits([
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'openNotificationPanel',
]);

const { t } = useI18n();
const searchShortcut = useKbd([`$mod`, 'k']);
const { accountId } = useAccount();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');

const notificationRoute = computed(() => ({
  name: 'notifications_index',
  params: { accountId: accountId.value },
}));
</script>

<template>
  <header
    class="sticky top-0 z-30 flex items-center justify-between h-14 px-4 bg-n-solid-2 border-b border-n-weak flex-shrink-0"
  >
    <!-- Left side: Logo + Account Switcher -->
    <div class="flex items-center gap-3 min-w-0">
      <RouterLink
        :to="{ name: 'section_landing' }"
        class="flex items-center gap-2 flex-shrink-0"
      >
        <div class="grid place-content-center size-8">
          <Logo class="size-5" />
        </div>
      </RouterLink>
      <div class="flex-shrink-0 w-px h-5 bg-n-strong" />
      <SidebarAccountSwitcher
        class="max-w-[200px]"
        @show-create-account-modal="emit('showCreateAccountModal')"
      />
    </div>

    <!-- Center: Search -->
    <div class="flex-1 max-w-md mx-4">
      <RouterLink
        :to="{ name: 'search' }"
        class="flex gap-2 items-center px-3 py-1.5 w-full h-9 rounded-lg outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30 hover:bg-n-alpha-1 transition-colors"
      >
        <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-11" />
        <span class="flex-grow text-left text-n-slate-11 text-sm">
          {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
        </span>
        <span
          class="hidden md:block tracking-wide pointer-events-none select-none text-n-slate-10 text-xs"
        >
          {{ searchShortcut }}
        </span>
      </RouterLink>
    </div>

    <!-- Right side: Notifications + Profile -->
    <div class="flex items-center gap-2">
      <div class="flex-shrink-0 w-px h-5 bg-n-strong" />

      <!-- Profile dropdown - wrapper overrides dropdown position to open downward -->
      <div class="relative [&_.absolute]:!top-full [&_.absolute]:!bottom-auto [&_.absolute]:!mt-2 [&_.absolute]:!mb-0 [&_.absolute]:!right-0 [&_.absolute]:!left-auto">
        <SidebarProfileMenu
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </div>
  </header>
</template>
